import { Account, TierExitPolicyConfig } from '../types';

export type RiskTierRow = NonNullable<Account['riskTiers']>[number];

export const DEFAULT_CHOP_EXIT_POLICY: Required<
  Pick<TierExitPolicyConfig, 'takeProfitPct' | 'maxHoldHours' | 'autoClose'>
> = {
  takeProfitPct: 50,
  maxHoldHours: 6,
  autoClose: true,
};

export const DEFAULT_DIRECTIONAL_ROLL_POLICY = {
  enabled: true,
  rollBudgetMode: 'original_basis' as const,
  rollTriggerMode: 'peak' as const,
  peakRollPct: 150,
  minLockedProfitPctOfBasis: 50,
  targetDteMin: 7,
  targetDteMax: 45,
  targetDte: 14,
  autoRoll: false,
};

export const DEFAULT_DIRECTIONAL_EXIT_POLICY: Required<
  Pick<TierExitPolicyConfig, 'takeProfitPct' | 'maxHoldTradingDays' | 'autoClose'>
> & { roll: typeof DEFAULT_DIRECTIONAL_ROLL_POLICY } = {
  takeProfitPct: 100,
  maxHoldTradingDays: 3,
  autoClose: false,
  roll: { ...DEFAULT_DIRECTIONAL_ROLL_POLICY },
};

export const DEFAULT_TIER1_ALLOC = 0.4;
export const DEFAULT_TIER6_ALLOC = 0.6;

export function isTier1MacroSlot(t: RiskTierRow): boolean {
  return (
    t.id.startsWith('equity_1_') ||
    t.strategy === 'macro_etf_portfolio' ||
    (t.riskTier === 1 &&
      t.assetType === 'EQUITY' &&
      t.thesisTimeframe === '6_months_plus')
  );
}

export function isTier6OptionsSlot(t: RiskTierRow): boolean {
  return (
    t.id.startsWith('option_6_') ||
    (t.riskTier === 6 &&
      t.assetType === 'OPTION' &&
      t.strategy !== 'macro_etf_portfolio')
  );
}

export function findTier1MacroTier(tiers?: RiskTierRow[]): RiskTierRow | undefined {
  return tiers?.find(isTier1MacroSlot);
}

export function findTier6OptionsTier(tiers?: RiskTierRow[]): RiskTierRow | undefined {
  return tiers?.find(isTier6OptionsSlot);
}

export function sumEnabledAllocationPct(tiers: RiskTierRow[]): number {
  return tiers.filter((t) => t.enabled).reduce((s, t) => s + t.allocationPct, 0);
}

/** Mirrors backend NAV × maxUtil × tier% deployable model (display only). */
export function previewDeployableCapital(
  navUsd: number,
  maxUtilization: number,
  slots: Array<{ enabled: boolean; allocationPct: number }>,
): {
  deployableUsd: number;
  cashReserveUsd: number;
  tierUsd: number[];
} {
  const nav = Math.max(0, navUsd);
  const deployableUsd = nav * Math.min(1, Math.max(0, maxUtilization));
  const tierUsd = slots.map((s) =>
    s.enabled ? deployableUsd * s.allocationPct : 0,
  );
  return {
    deployableUsd,
    cashReserveUsd: Math.max(0, nav - deployableUsd),
    tierUsd,
  };
}
